import React, { useEffect } from 'react';
import { 
  Leaf, Sun, Droplets, Wind, TreePine, Recycle,
  Globe, Target, TrendingDown, Award, Zap, Heart,
  ArrowRight, CheckCircle, BarChart3
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const SustentabilidadPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Impacto ambiental de cada panel de 600W instalado (anualmente)
  const impacts = [
    {
      icon: TrendingDown,
      value: '~0.5',
      unit: 'Toneladas CO2',
      description: 'Evitadas por año',
      color: 'emerald'
    },
    {
      icon: Sun,
      value: '~1,095',
      unit: 'kWh generados',
      description: 'Energía limpia anual',
      color: 'yellow'
    },
    {
      icon: TreePine,
      value: '~25',
      unit: 'Árboles equivalentes',
      description: 'Plantados por año',
      color: 'green'
    },
    {
      icon: Droplets,
      value: '~1,500',
      unit: 'Litros de agua',
      description: 'Ahorrados en generación',
      color: 'blue'
    }
  ];

  const sdgs = [
    { number: 7, name: 'Energía Asequible y No Contaminante', color: 'bg-yellow-500' },
    { number: 11, name: 'Ciudades y Comunidades Sostenibles', color: 'bg-amber-600' },
    { number: 12, name: 'Producción y Consumo Responsables', color: 'bg-amber-700' },
    { number: 13, name: 'Acción por el Clima', color: 'bg-emerald-600' }
  ];

  const commitments = [
    {
      title: 'Operaciones Sostenibles',
      items: [
        'Oficinas con energía 100% solar',
        'Vehículos eléctricos para instalaciones',
        'Política de cero papel',
        'Proveedores con certificación ambiental'
      ]
    },
    {
      title: 'Productos Responsables',
      items: [
        'Solo marcas con certificación ISO 14001',
        'Paneles con bajo carbono incorporado',
        'Empaques reciclables',
        'Garantía de larga duración'
      ]
    },
    {
      title: 'Impacto Comunitario',
      items: [
        'Empleo local prioritario',
        'Capacitación de técnicos paraguayos',
        'Precios accesibles para todos'
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-100 text-emerald-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600'
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sustentabilidad-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-green-800 via-emerald-700 to-teal-800 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-300 mb-6">
            <Leaf className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Compromiso Ambiental</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Sustentabilidad
            <span className="block text-emerald-300">Más que un Compromiso, una Forma de Vida</span>
          </h1>
          
          <p className="text-xl text-emerald-100 max-w-2xl">
            Cada panel solar que instalamos es un paso hacia un Paraguay más limpio. 
            Conocé cómo medimos y maximizamos nuestro impacto positivo.
          </p>
        </div>
      </section>

      {/* Impact Stats - Per Panel */}
      <section className="py-12 bg-white relative -mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h3 className="text-lg font-semibold text-emerald-600">Impacto de cada panel de 600W instalado</h3>
            <p className="text-sm text-zinc-500">Contribución ambiental anual por panel</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impacts.map((impact, index) => {
              const Icon = impact.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center border border-zinc-100">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${getColorClasses(impact.color)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-3xl font-bold text-zinc-900">{impact.value}</div>
                  <div className="text-sm text-emerald-600 font-medium">{impact.unit}</div>
                  <p className="text-xs text-zinc-500 mt-1">{impact.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Nuestro Enfoque
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              La sustentabilidad está integrada en todo lo que hacemos
            </p>
          </div>

          <div className="prose prose-lg max-w-none text-center">
            <p className="text-zinc-600">
              En Zenex entendemos que no basta con instalar paneles solares. La verdadera sustentabilidad 
              implica considerar todo el ciclo de vida de los productos, minimizar nuestra huella operativa, 
              y contribuir activamente al desarrollo de las comunidades donde trabajamos.
            </p>
          </div>
        </div>
      </section>

      {/* SDGs */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Objetivos de Desarrollo Sostenible
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Nuestro trabajo contribuye directamente a los ODS de las Naciones Unidas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {sdgs.map((sdg, index) => (
              <div 
                key={index}
                className={`${sdg.color} rounded-xl p-6 text-center text-white`}
              >
                <div className="text-4xl font-bold mb-2">ODS {sdg.number}</div>
                <p className="text-sm opacity-90">{sdg.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Nuestros Compromisos
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {commitments.map((commitment, index) => (
              <div key={index} className="p-6 bg-zinc-50 rounded-xl">
                <h3 className="text-xl font-bold text-zinc-900 mb-4">{commitment.title}</h3>
                <ul className="space-y-3">
                  {commitment.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-zinc-600">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="w-16 h-16 mx-auto text-white/80 mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            Sé parte del cambio
          </h2>
          <p className="text-green-100 mb-8">
            Cada sistema solar instalado es una victoria para el planeta
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all"
            >
              Calcular Mi Impacto
            </button>
            <Link 
              to="/proyectos/accion-social"
              className="px-8 py-4 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all"
            >
              Ver Acción Social
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SustentabilidadPage;
